import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUploadBatch } from './useUploadBatch';
import { useHubUpload, HubUploadStage, UploadTargetContext } from './useHubUpload';
import { uploadBatchApi } from 'src/lib/api/files';
import { BatchFileState, BatchUploadFile, LocalFile, UploadPhase } from './types';
import { isFailed, isReadyForUse } from './utils';

// ---------------------------------------------------------------------------
// Stage → status mapping (mirrors useUploadBatch.ts)
// ---------------------------------------------------------------------------

const STAGE_TO_STATUS: Record<HubUploadStage, BatchFileState['status']> = {
  uploading: 'pending_upload',
  scanning: 'security_scanning',
  processing: 'clean',
  ready: 'ready',
  error: 'failed'
};

// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------

const HUB_BATCH_KEY = 'hub_background_batches';
const MAX_BATCH_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours

interface PersistedBatch {
  batchId: string;
  savedAt: number;
  files: { fileId: string; name: string; size: number }[];
}

function loadPersistedBatches(): PersistedBatch[] {
  try {
    const raw = localStorage.getItem(HUB_BATCH_KEY);
    if (!raw) return [];
    const batches = JSON.parse(raw) as PersistedBatch[];
    const cutoff = Date.now() - MAX_BATCH_AGE_MS;
    return batches.filter((b) => b.savedAt > cutoff);
  } catch {
    return [];
  }
}

function savePersistedBatches(batches: PersistedBatch[]): void {
  try {
    if (batches.length === 0) {
      localStorage.removeItem(HUB_BATCH_KEY);
    } else {
      localStorage.setItem(HUB_BATCH_KEY, JSON.stringify(batches));
    }
  } catch {}
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StartBackgroundUploadOptions extends UploadTargetContext {
  documentType?: string;
}

export interface UploadBatchContextType {
  phase: UploadPhase;
  localFiles: LocalFile[];
  batchId: string | null;
  batchFiles: BatchFileState[];
  addFiles: (files: File[]) => Promise<void>;
  removeLocalFile: (key: string) => void;
  startUpload: () => void;
  reset: () => void;
  uploading: boolean;
  uploadError: string | null;
  /** Live files being tracked by the Document Hub progress widget. */
  hubBatchFiles: BatchFileState[];
  /**
   * Start a background upload that survives page navigation and refresh.
   * The upload runs in the app-level context.
   * Returns the opaque per-file keys that can be used to track progress in
   * ``hubBatchFiles``.
   * onComplete is called once all files reach a terminal state.
   */
  startBackgroundUpload: (
    files: File[],
    options: StartBackgroundUploadOptions,
    onComplete: (readyFiles: BatchUploadFile[]) => void
  ) => string[];
  /** Remove all completed hub files from the widget (user-initiated dismiss). */
  dismissHubBatchFiles: () => void;
  /** Abort one in-flight hub file and drop it from the tray. */
  stopHubFile: (key: string) => void;
  /**
   * Hide a batch restored into the progress widget and forget it.
   * In-progress snapshots are not resumable after reload, so dismissing is
   * the only way to clear a widget that would otherwise stay at 0%.
   */
  dismissTracking: () => void;
  /** Drop one legacy batch file from the tray without clearing the others. */
  dismissBatchFile: (key: string) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const UploadBatchContext = createContext<UploadBatchContextType | undefined>(undefined);

const POLL_INTERVAL_MS = 3000;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const UploadBatchProvider = ({ children }: { children: React.ReactNode }) => {
  // Global upload batch (legacy / chat uploads)
  const {
    phase,
    localFiles,
    batchId,
    batchFiles,
    addFiles,
    removeLocalFile,
    startUpload,
    reset,
    uploading,
    uploadError,
    dismissTracking,
    dismissBatchFile
  } = useUploadBatch();

  // ── Resumed-batch state (restored from localStorage on mount) ─────────────
  // Maps fileId → BatchFileState for batches that were in-flight at last
  // refresh.  Polled independently of backgroundHub.
  const [resumedFiles, setResumedFiles] = useState<Map<string, BatchFileState>>(new Map());
  const resumedBatchesRef = useRef<PersistedBatch[]>([]);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current !== null) return;
    pollTimerRef.current = setInterval(async () => {
      const batches = resumedBatchesRef.current;
      if (batches.length === 0) {
        stopPolling();
        return;
      }

      const nextBatches: PersistedBatch[] = [];
      const updates = new Map<string, BatchFileState>();

      for (const batch of batches) {
        try {
          const apiFiles = await uploadBatchApi.getBatchFiles(batch.batchId);
          let batchDone = true;

          for (const af of apiFiles) {
            const state: BatchFileState = {
              fileId: af.id,
              key: af.id,
              name: af.name,
              size: af.size,
              status: af.status,
              uploadProgress: 100,
              errorMessage: af.error_message ?? undefined
            };
            updates.set(af.id, state);
            if (!isReadyForUse(af.status) && !isFailed(af.status)) {
              batchDone = false;
            }
          }

          if (!batchDone) {
            nextBatches.push(batch);
          }
          // Done batches are removed from the poll list but their files stay
          // visible in the widget until the user dismisses.
        } catch {
          // Keep polling on transient errors
          nextBatches.push(batch);
        }
      }

      setResumedFiles((prev) => {
        if (updates.size === 0) return prev;
        const next = new Map(prev);
        updates.forEach((v, k) => next.set(k, v));
        return next;
      });

      if (nextBatches.length !== batches.length) {
        resumedBatchesRef.current = nextBatches;
        savePersistedBatches(nextBatches);
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  // On mount: restore batches saved by the previous session and resume polling.
  useEffect(() => {
    const saved = loadPersistedBatches();
    if (saved.length === 0) return;

    resumedBatchesRef.current = saved;

    // Show placeholder states immediately (status unknown → show as scanning)
    const initial = new Map<string, BatchFileState>();
    for (const batch of saved) {
      for (const f of batch.files) {
        initial.set(f.fileId, {
          fileId: f.fileId,
          key: f.fileId,
          name: f.name,
          size: f.size,
          status: 'security_scanning', // best guess until first poll
          uploadProgress: 100
        });
      }
    }
    setResumedFiles(initial);
    startPolling();

    return stopPolling;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persistent app-level hub instance for current-session uploads ─────────
  const backgroundHub = useHubUpload({
    skipDI: () => true,
    onBatchReady: useCallback(
      (batchIdStr: string, files: { key: string; fileId: string; name: string; size: number }[]) => {
        const batch: PersistedBatch = {
          batchId: batchIdStr,
          savedAt: Date.now(),
          files: files.map(({ fileId, name, size }) => ({ fileId, name, size }))
        };
        resumedBatchesRef.current = [...resumedBatchesRef.current, batch];
        savePersistedBatches(resumedBatchesRef.current);
        // Polling is already running via the backgroundHub state; if somehow
        // it isn't, start it so resumed-batch polling also kicks in.
        startPolling();
      },
      [startPolling]
    )
  });

  // Derive live hubBatchFiles from backgroundHub.uploads
  const liveBatchFiles = useMemo<BatchFileState[]>(
    () =>
      Array.from(backgroundHub.uploads.entries()).map(([key, hs]) => ({
        fileId: hs.documentId ?? key,
        key,
        name: hs.file.name,
        size: hs.file.size,
        status: STAGE_TO_STATUS[hs.stage],
        uploadProgress: hs.stage === 'uploading' ? hs.uploadProgress : 100,
        errorMessage: hs.error
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backgroundHub.uploads]
  );

  // Combined widget files: live (current session) + resumed (from previous session).
  // Live files shadow resumed files with the same fileId so we never double-show.
  const hubBatchFiles = useMemo<BatchFileState[]>(() => {
    if (resumedFiles.size === 0) return liveBatchFiles;
    const liveIds = new Set(liveBatchFiles.map((f) => f.fileId));
    const resumed = Array.from(resumedFiles.values()).filter((f) => !liveIds.has(f.fileId));
    return [...liveBatchFiles, ...resumed];
  }, [liveBatchFiles, resumedFiles]);

  // ── Pending-completion callbacks for current-session batches ──────────────
  interface PendingBatch {
    keys: string[];
    onComplete: (readyFiles: BatchUploadFile[]) => void;
  }
  const pendingBatchesRef = useRef<PendingBatch[]>([]);

  useEffect(() => {
    const uploads = backgroundHub.uploads;
    const remaining: PendingBatch[] = [];

    for (const batch of pendingBatchesRef.current) {
      const allDone = batch.keys.every((key) => {
        const hs = uploads.get(key);
        return !hs || hs.stage === 'ready' || hs.stage === 'error';
      });

      if (allDone) {
        const readyFiles: BatchUploadFile[] = batch.keys
          .filter((key) => uploads.get(key)?.stage === 'ready')
          .map((key) => {
            const hs = uploads.get(key)!;
            return { fileId: hs.documentId ?? key, name: hs.file.name, size: hs.file.size };
          });

        batch.onComplete(readyFiles);
        // Files stay visible until the user dismisses via the X button.
      } else {
        remaining.push(batch);
      }
    }

    pendingBatchesRef.current = remaining;
  }, [backgroundHub.uploads]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissHubBatchFiles = useCallback(() => {
    // Clear live files from backgroundHub
    backgroundHub.clearUploads({ revokePreviewUrls: false });
    // Clear resumed files and localStorage
    setResumedFiles(new Map());
    resumedBatchesRef.current = [];
    savePersistedBatches([]);
  }, [backgroundHub]);

  const stopHubFile = useCallback(
    (key: string) => {
      const upload = backgroundHub.getUploads().get(key);
      const restored = resumedBatchesRef.current.flatMap((batch) => batch.files).find((file) => file.fileId === key);
      const fileId = upload?.documentId ?? restored?.fileId;
      backgroundHub.removeFile(key);
      if (fileId) uploadBatchApi.cancelFile(fileId).catch(() => {});
      setResumedFiles((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [id, file] of prev) {
          if (file.key === key || id === key) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
      const batches = resumedBatchesRef.current
        .map((batch) => ({
          ...batch,
          files: batch.files.filter((file) => file.fileId !== key && file.fileId !== fileId)
        }))
        .filter((batch) => batch.files.length > 0);
      resumedBatchesRef.current = batches;
      savePersistedBatches(batches);
    },
    [backgroundHub]
  );

  const startBackgroundUpload = useCallback(
    (
      files: File[],
      options: StartBackgroundUploadOptions,
      onComplete: (readyFiles: BatchUploadFile[]) => void
    ): string[] => {
      const keys = backgroundHub.uploadFiles(files, {
        documentType: options.documentType,
        groupId: options.groupId,
        pamQuestionId: options.pamQuestionId,
        pamSiteId: options.pamSiteId,
        addToOrganisation: options.addToOrganisation
      });
      pendingBatchesRef.current.push({ keys, onComplete });
      return keys;
    },
    [backgroundHub]
  );

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'uploading') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  const value = useMemo<UploadBatchContextType>(
    () => ({
      phase,
      localFiles,
      batchId,
      batchFiles,
      addFiles,
      removeLocalFile,
      startUpload,
      reset,
      uploading,
      uploadError,
      hubBatchFiles,
      startBackgroundUpload,
      dismissHubBatchFiles,
      stopHubFile,
      dismissTracking,
      dismissBatchFile
    }),
    [
      phase,
      localFiles,
      batchId,
      batchFiles,
      addFiles,
      removeLocalFile,
      startUpload,
      reset,
      uploading,
      uploadError,
      hubBatchFiles,
      startBackgroundUpload,
      dismissHubBatchFiles,
      stopHubFile,
      dismissTracking,
      dismissBatchFile
    ]
  );

  return <UploadBatchContext.Provider value={value}>{children}</UploadBatchContext.Provider>;
};

export const useUploadBatchContext = (): UploadBatchContextType => {
  const ctx = useContext(UploadBatchContext);
  if (ctx === undefined) {
    throw new Error('useUploadBatchContext must be used within UploadBatchProvider');
  }
  return ctx;
};
